import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Chip, TextField, InputAdornment,
    Select, MenuItem, FormControl, InputLabel, IconButton,
    Tooltip, useTheme,
} from '@mui/material';
import {
    Search as SearchIcon,
    ArrowUpward as AscIcon,
    ArrowDownward as DescIcon,
    AccessTime as PendingActionIcon,
} from '@mui/icons-material';
import { RootState, Ticket, TicketPriority, TicketStatus } from '../../types';
import { fetchTickets } from '../../store/slices/ticketsSlice';
import { AppDispatch } from '../../store';
import { DataTable, Column } from '../../components/common';
import { formatDate, isDatePast, isDateToday } from '../../utils/dateUtils';
import { getSeverityChipSx, getStatusChipSx, getStatusColor, getPriorityColor } from '../../theme/mappers/ticketMappers';
import { colorUtils } from '../../theme';
import { isTicketUnread } from '../../utils/ticketViews';

// ── Types ──────────────────────────────────────────────────────────────────────

type StatusFilter = TicketStatus | 'all' | 'pending_action';
type PriorityFilter = TicketPriority | 'all';
type DateRange = 'all' | '7d' | '30d' | '90d';
type SortKey = 'created_at' | 'due_date' | 'priority' | 'status' | 'title';
type SortDir = 'asc' | 'desc';

interface FilterState {
    search: string;
    status: StatusFilter;
    priority: PriorityFilter;
    dateRange: DateRange;
    sortKey: SortKey;
    sortDir: SortDir;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

const PRIORITY_ORDER: Record<TicketPriority, number> = {
    [TicketPriority.CRITICAL]: 0,
    [TicketPriority.HIGH]: 1,
    [TicketPriority.MEDIUM]: 2,
    [TicketPriority.LOW]: 3,
};

const generateDueDate = (createdAt: string, priority: TicketPriority): string => {
    const slaHours: Record<TicketPriority, number> = {
        [TicketPriority.CRITICAL]: 4,
        [TicketPriority.HIGH]: 24,
        [TicketPriority.MEDIUM]: 72,
        [TicketPriority.LOW]: 168,
    };
    const due = new Date(createdAt);
    due.setHours(due.getHours() + slaHours[priority]);
    return due.toISOString();
};

// ── Status chip config ─────────────────────────────────────────────────────────

const STATUS_TABS: { value: StatusFilter; label: string }[] = [
    { value: 'all',                         label: 'All' },
    { value: TicketStatus.NEW,              label: 'New' },
    { value: TicketStatus.ASSIGNED,         label: 'Assigned' },
    { value: TicketStatus.IN_PROGRESS,      label: 'In Progress' },
    { value: 'pending_action',              label: 'Pending my action' },
    { value: TicketStatus.ESCALATED,        label: 'Escalated' },
    { value: TicketStatus.RESOLVED,         label: 'Resolved' },
    { value: TicketStatus.CLOSED,           label: 'Closed' },
];

const PRIORITY_TABS: { value: PriorityFilter; label: string }[] = [
    { value: 'all',                         label: 'All priorities' },
    { value: TicketPriority.CRITICAL,       label: 'Critical' },
    { value: TicketPriority.HIGH,           label: 'High' },
    { value: TicketPriority.MEDIUM,         label: 'Medium' },
    { value: TicketPriority.LOW,            label: 'Low' },
];

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
    { value: 'created_at',  label: 'Date opened' },
    { value: 'due_date',    label: 'SLA due' },
    { value: 'priority',    label: 'Priority' },
    { value: 'status',      label: 'Status' },
    { value: 'title',       label: 'Title' },
];

// ── Page ───────────────────────────────────────────────────────────────────────

const ClientTicketsPage: React.FC = () => {
    const theme = useTheme();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { tickets, isLoading, error } = useSelector((state: RootState) => state.tickets);

    // All filter + sort state in one object.
    // Shape intentionally mirrors future API params so the swap is trivial:
    //   fetchTickets({ status, priority, sort_by: sortKey, sort_dir: sortDir, ... })
    const [filters, setFilters] = useState<FilterState>({
        search: '',
        status: 'all',
        priority: 'all',
        dateRange: 'all',
        sortKey: 'created_at',
        sortDir: 'desc',
    });

    const set = <K extends keyof FilterState>(key: K, value: FilterState[K]) =>
        setFilters(prev => ({ ...prev, [key]: value }));

    useEffect(() => {
        // TODO (backend): when API supports filtering/sorting, pass filters here instead:
        // dispatch(fetchTickets({ status: filters.status, priority: filters.priority, ... }))
        // and delete the client-side useMemo block below.
        dispatch(fetchTickets({}));
    }, [dispatch]);

    // Enrich with computed due_date
    const enriched = useMemo(() =>
        tickets.map(t => ({
            ...t,
            due_date: t.due_date || generateDueDate(t.created_at, t.priority),
        })),
    [tickets]);

    // ── CLIENT-SIDE FILTER + SORT ──────────────────────────────────────────────
    // TODO (backend): remove this block once fetchTickets() handles filtering/sorting.
    const filtered = useMemo(() => {
        const now = new Date();
        const cutoffs: Record<DateRange, number> = {
            all: 0,
            '7d':  7  * 24 * 60 * 60 * 1000,
            '30d': 30 * 24 * 60 * 60 * 1000,
            '90d': 90 * 24 * 60 * 60 * 1000,
        };

        let result = enriched.filter(t => {
            // Search
            if (filters.search.trim()) {
                const q = filters.search.toLowerCase();
                if (
                    !t.title.toLowerCase().includes(q) &&
                    !t.category?.toLowerCase().includes(q) &&
                    !t.id.toLowerCase().includes(q)
                ) return false;
            }

            // Status
            if (filters.status === 'pending_action') {
                if (t.status !== TicketStatus.PENDING) return false;
            } else if (filters.status !== 'all') {
                if (t.status !== filters.status) return false;
            }

            // Priority
            if (filters.priority !== 'all' && t.priority !== filters.priority) return false;

            // Date range
            if (filters.dateRange !== 'all') {
                const age = now.getTime() - new Date(t.created_at).getTime();
                if (age > cutoffs[filters.dateRange]) return false;
            }

            return true;
        });

        // Sort
        result = [...result].sort((a, b) => {
            let cmp = 0;
            switch (filters.sortKey) {
                case 'priority':
                    cmp = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
                    break;
                case 'due_date':
                    cmp = new Date(a.due_date!).getTime() - new Date(b.due_date!).getTime();
                    break;
                case 'created_at':
                    cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                    break;
                case 'status':
                    cmp = a.status.localeCompare(b.status);
                    break;
                case 'title':
                    cmp = a.title.localeCompare(b.title);
                    break;
            }
            return filters.sortDir === 'asc' ? cmp : -cmp;
        });

        return result;
    }, [enriched, filters]);
    // ── END CLIENT-SIDE FILTER + SORT ─────────────────────────────────────────

    const getDueDateColor = (dueDate: string | null) => {
        if (!dueDate) return theme.palette.text.disabled;
        if (isDatePast(dueDate)) return theme.palette.severity.critical;
        if (isDateToday(dueDate)) return theme.palette.severity.medium;
        return theme.palette.text.secondary;
    };

    const activeCount = filtered.length;
    const totalCount = tickets.length;

    const columns: Column<Ticket>[] = [
        {
            key: 'id',
            header: 'ID',
            minWidth: 110,
            noWrap: true,
            align: 'left',
            render: (t) => (
                <Chip
                    label={t.id.slice(0, 8)}
                    size="small"
                    sx={{
                        height: 22, fontSize: '0.65rem', fontWeight: 700,
                        fontFamily: '"JetBrains Mono", monospace',
                        backgroundColor: 'transparent',
                        color: theme.palette.text.secondary,
                        border: `1px solid ${theme.palette.border.main}`,
                        '& .MuiChip-label': { px: 1 },
                    }}
                />
            ),
        },
        {
            key: 'title',
            header: 'Title',
            minWidth: 200,
            render: (t) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isTicketUnread(t.id, t.updated_at) && (
                        <Box sx={{
                            width: 7, height: 7, borderRadius: '50%',
                            backgroundColor: theme.palette.primary.main,
                            flexShrink: 0,
                        }} />
                    )}
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: theme.palette.text.primary }}>
                        {t.title}
                    </Typography>
                </Box>
            ),
        },
        {
            key: 'priority',
            header: 'Priority',
            minWidth: 90,
            noWrap: true,
            align: 'center',
            render: (t) => (
                <Chip
                    label={t.priority}
                    size="small"
                    sx={{
                        ...getSeverityChipSx(getPriorityColor(t.priority, theme)),
                        backgroundColor: colorUtils.withOpacity(getPriorityColor(t.priority, theme), 0.1),
                        '& .MuiChip-label': { px: 1 },
                    }}
                />
            ),
        },
        {
            key: 'status',
            header: 'Status',
            minWidth: 100,
            noWrap: true,
            align: 'center',
            render: (t) => (
                <Chip
                    label={t.status.replace(/_/g, ' ')}
                    size="small"
                    sx={getStatusChipSx(getStatusColor(t.status, theme))}
                />
            ),
        },
        {
            key: 'category',
            header: 'Category',
            minWidth: 130,
            noWrap: true,
            render: (t) => (
                <Typography sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary, textTransform: 'capitalize' }}>
                    {t.category?.replace(/_/g, ' ') || '—'}
                </Typography>
            ),
        },
        {
            key: 'created_at',
            header: 'Opened',
            minWidth: 140,
            noWrap: true,
            render: (t) => (
                <Typography sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary }}>
                    {formatDate(t.created_at)}
                </Typography>
            ),
        },
        {
            key: 'due_date',
            header: 'SLA Due',
            minWidth: 140,
            noWrap: true,
            render: (t) => (
                <Typography sx={{
                    fontSize: '0.8rem',
                    fontWeight: isDatePast(t.due_date!) ? 600 : 400,
                    color: getDueDateColor(t.due_date!),
                }}>
                    {t.due_date ? formatDate(t.due_date) : '—'}
                </Typography>
            ),
        },
    ];

    const filterChipSx = (active: boolean, color?: string) => ({
        height: 28,
        fontSize: '0.75rem',
        fontWeight: active ? 600 : 400,
        cursor: 'pointer',
        backgroundColor: active
            ? color
                ? colorUtils.withOpacity(color, 0.15)
                : colorUtils.withOpacity(theme.palette.primary.main, 0.15)
            : 'transparent',
        color: active
            ? color || theme.palette.primary.main
            : theme.palette.text.secondary,
        border: `1px solid ${active
            ? color
                ? colorUtils.withOpacity(color, 0.4)
                : colorUtils.withOpacity(theme.palette.primary.main, 0.4)
            : theme.palette.border.main}`,
        '&:hover': {
            backgroundColor: active
                ? undefined
                : theme.palette.interactive.hover,
        },
    });

    const priorityChipColor = (p: PriorityFilter) =>
        p === 'all' ? undefined : getPriorityColor(p as TicketPriority, theme);

    return (
        <Box sx={{ minHeight: '100vh', px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>

            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h1" sx={{ color: theme.palette.text.primary, fontWeight: 600, mb: 0.5 }}>
                    My Incidents
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                    {activeCount === totalCount
                        ? `${totalCount} incident${totalCount !== 1 ? 's' : ''} total`
                        : `${activeCount} of ${totalCount} incidents`}
                </Typography>
            </Box>

            {/* Toolbar row: search + sort controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    placeholder="Search incidents..."
                    value={filters.search}
                    onChange={(e) => set('search', e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 18, color: theme.palette.text.tertiary }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        width: 280,
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.palette.background.tertiary,
                            height: 40,
                            '& fieldset': { border: 'none' },
                            '&:hover': { backgroundColor: theme.palette.background.secondary },
                            '&.Mui-focused': {
                                backgroundColor: theme.palette.background.default,
                                boxShadow: `0 0 0 1px ${theme.palette.text.primary}`,
                            },
                        },
                    }}
                />

                {/* Date range */}
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <Select
                        value={filters.dateRange}
                        onChange={(e) => set('dateRange', e.target.value as DateRange)}
                        displayEmpty
                        sx={{
                            height: 40,
                            backgroundColor: theme.palette.background.tertiary,
                            '& fieldset': { border: 'none' },
                            '&:hover': { backgroundColor: theme.palette.background.secondary },
                            fontSize: '0.85rem',
                        }}
                    >
                        <MenuItem value="all">All time</MenuItem>
                        <MenuItem value="7d">Last 7 days</MenuItem>
                        <MenuItem value="30d">Last 30 days</MenuItem>
                        <MenuItem value="90d">Last 90 days</MenuItem>
                    </Select>
                </FormControl>

                {/* Sort key */}
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                        value={filters.sortKey}
                        onChange={(e) => set('sortKey', e.target.value as SortKey)}
                        sx={{
                            height: 40,
                            backgroundColor: theme.palette.background.tertiary,
                            '& fieldset': { border: 'none' },
                            '&:hover': { backgroundColor: theme.palette.background.secondary },
                            fontSize: '0.85rem',
                        }}
                    >
                        {SORT_OPTIONS.map(o => (
                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Sort direction toggle */}
                <Tooltip title={filters.sortDir === 'asc' ? 'Ascending' : 'Descending'}>
                    <IconButton
                        size="small"
                        onClick={() => set('sortDir', filters.sortDir === 'asc' ? 'desc' : 'asc')}
                        sx={{
                            border: `1px solid ${theme.palette.border.main}`,
                            borderRadius: 1,
                            width: 40, height: 40,
                            color: theme.palette.text.secondary,
                            backgroundColor: theme.palette.background.tertiary,
                            '&:hover': { backgroundColor: theme.palette.background.secondary },
                        }}
                    >
                        {filters.sortDir === 'asc'
                            ? <AscIcon sx={{ fontSize: 18 }} />
                            : <DescIcon sx={{ fontSize: 18 }} />
                        }
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Status filter chips */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                {STATUS_TABS.map(tab => (
                    <Chip
                        key={tab.value}
                        label={tab.value === 'pending_action'
                            ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PendingActionIcon sx={{ fontSize: 13 }} />
                                {tab.label}
                              </Box>
                            : tab.label
                        }
                        size="small"
                        onClick={() => set('status', tab.value)}
                        sx={filterChipSx(
                            filters.status === tab.value,
                            tab.value === 'pending_action' ? theme.palette.severity.medium : undefined,
                        )}
                    />
                ))}
            </Box>

            {/* Priority filter chips */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                {PRIORITY_TABS.map(tab => (
                    <Chip
                        key={tab.value}
                        label={tab.label}
                        size="small"
                        onClick={() => set('priority', tab.value)}
                        sx={filterChipSx(filters.priority === tab.value, priorityChipColor(tab.value))}
                    />
                ))}
            </Box>

            <DataTable
                columns={columns}
                data={filtered}
                rowKey={(t) => t.id}
                isLoading={isLoading}
                error={error}
                emptyMessage="No incidents found"
                emptySubMessage={
                    filters.search || filters.status !== 'all' || filters.priority !== 'all' || filters.dateRange !== 'all'
                        ? 'Try adjusting your filters'
                        : 'No incidents have been created for your account yet'
                }
                loadingMessage="Loading incidents..."
                onRowClick={(t) => navigate(`/client/tickets/${t.id}`)}
                rowHeight={52}
                minTableWidth={700}
            />
        </Box>
    );
};

export default ClientTicketsPage;
